import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { apiGet } from "../api/client";
import type { OrganizationResponse, PlayerResponse } from "../api/legacyTypes";
import { displayPersonName } from "../utils/personName";
import { entitySlug } from "../utils/slug";
import { CountryMark } from "./CountryMark";
import { EntityLogo } from "./EntityLogo";

interface EntityLinkProps {
  gameId: string;
  kind: "player" | "team";
  name: string;
  entityId?: string;
  logoUrl?: string;
  className?: string;
  showLogo?: boolean;
}

export function EntityLink({ gameId, kind, name, entityId, logoUrl, className, showLogo = true }: EntityLinkProps) {
  const [open, setOpen] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  const slug = entityId?.split("/").at(-1) || entitySlug(name);
  const resource = kind === "player" ? "players" : "teams";
  const href = `/${resource}/${gameId}/${slug}`;
  const endpoint = `/api/v1/${resource}/${gameId}/${slug}`;
  const query = useQuery({
    queryKey: ["entity-preview", kind, gameId, slug],
    queryFn: ({ signal }) => apiGet<PlayerResponse | OrganizationResponse>(endpoint, signal),
    enabled: open,
    staleTime: 5 * 60_000,
  });
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const showLater = () => { timer.current = window.setTimeout(() => setOpen(true), 500); };
  const hide = () => { window.clearTimeout(timer.current); setOpen(false); };
  const player = query.data && "player" in query.data ? query.data.player : undefined;
  const team = query.data && "organization" in query.data ? query.data.organization : undefined;

  return (
    <span className={`entity-link-wrap ${className ?? ""}`} onMouseEnter={showLater} onMouseLeave={hide}>
      <a className="entity-link" href={href} title={name} onFocus={() => setOpen(true)} onBlur={hide}>
        {showLogo && (logoUrl || kind === "team") && <EntityLogo src={logoUrl || `/media/organization/${gameId}/${slug}`} name={name} />}
        <span>{name}</span>
      </a>
      {open && (
        <aside className="entity-preview" role="tooltip">
          {query.isPending && <span>Loading preview…</span>}
          {query.isError && <span>Preview is not cached yet.</span>}
          {player && <>
            <img src={`/media/player/${gameId}/${slug}`} alt="" />
            <div><small>{gameId} player</small><strong>{player.handle}</strong><span>{player.realName ? displayPersonName(player.realName) : "Real name not listed"}</span><span><CountryMark country={player.country} /> {player.country}</span><em>{player.organizationName || "Independent"}</em></div>
          </>}
          {team && <>
            <img src={`/media/organization/${gameId}/${slug}`} alt="" />
            <div><small>{gameId} team</small><strong>{team.name}</strong><span>{team.location || team.region || "Location not listed"}</span><em>Open team page</em></div>
          </>}
        </aside>
      )}
    </span>
  );
}
